import React, { useEffect, useState } from 'react';
import { Mail, Loader2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import {
  OverdueNotice,
  OverdueNoticeFilters,
  NoticeType,
  overdueNoticesAPI,
} from '@/services/library-mgmt-api';

const NOTICE_TYPES: NoticeType[] = ['first_notice', 'second_notice', 'final_notice', 'reminder'];
const STATUS_OPTIONS = ['pending', 'sent', 'delivered', 'failed', 'bounced'];

const NOTICE_LABEL: Record<string, string> = {
  first_notice: 'First notice',
  second_notice: 'Second notice',
  final_notice: 'Final notice',
  reminder: 'Reminder',
};

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] border-[var(--lib-gold)]/30',
  sent: 'bg-[var(--lib-navy)]/10 text-[var(--lib-navy)] border-[var(--lib-navy)]/30',
  delivered: 'bg-[var(--lib-success)]/10 text-[var(--lib-success)] border-[var(--lib-success)]/30',
  failed: 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30',
  bounced: 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30',
};

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const OverdueNoticesModule: React.FC = () => {
  const [items, setItems] = useState<OverdueNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [noticeType, setNoticeType] = useState<NoticeType | ''>('');
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const filters: OverdueNoticeFilters = { page_size: 100 };
    if (noticeType) filters.notice_type = noticeType;
    if (status) filters.status = status;
    overdueNoticesAPI
      .list(filters)
      .then((data) => {
        if (!cancel) setItems(data.results);
      })
      .catch(() => {
        if (!cancel) setItems([]);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => { cancel = true; };
  }, [noticeType, status]);

  const failedCount = items.filter((i) => i.delivery_status === 'failed' || i.delivery_status === 'bounced').length;
  const deliveredCount = items.filter((i) => i.delivery_status === 'delivered' || i.delivery_status === 'sent').length;

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)] flex items-center gap-2">
            <Mail className="h-6 w-6 text-[var(--lib-gold)]" />
            Overdue Notices
          </h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">
            History of notices sent to borrowers. Send new notices from the Loans page.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg">
        <div className="grid grid-cols-3 divide-x divide-[var(--lib-hairline)]">
          <Kpi label="Notices" value={items.length.toLocaleString()} />
          <Kpi label="Delivered/Sent" value={deliveredCount.toLocaleString()} tone="ok" />
          <Kpi label="Failed/Bounced" value={failedCount.toLocaleString()} tone={failedCount > 0 ? 'red' : 'ok'} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={noticeType}
          onChange={(e) => setNoticeType(e.target.value as NoticeType | '')}
          className="text-sm h-9 px-3 rounded-lg bg-[var(--lib-surface)] border border-[var(--lib-hairline)] text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)]"
        >
          <option value="">All notice types</option>
          {NOTICE_TYPES.map((n) => (
            <option key={n} value={n}>{NOTICE_LABEL[n]}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="text-sm h-9 px-3 rounded-lg bg-[var(--lib-surface)] border border-[var(--lib-hairline)] text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)]"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-[var(--lib-ink-muted)] text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-[var(--lib-ink-muted)]">No overdue notices for these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]">
                <Th>Sent</Th>
                <Th>Notice</Th>
                <Th>Borrower</Th>
                <Th>Book</Th>
                <Th className="text-right">Days overdue</Th>
                <Th className="text-right">Fine</Th>
                <Th>Delivery</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--lib-hairline)]">
              {items.map((n) => (
                <tr key={n.id} className="hover:bg-[var(--lib-surface-2)] transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] whitespace-nowrap">{fmtDate(n.sent_at)}</td>
                  <td className="px-5 py-3 text-[var(--lib-ink)]">
                    {NOTICE_LABEL[n.notice_type] ?? n.notice_type}
                    {n.sent_by_name && (
                      <div className="text-[11px] font-mono text-[var(--lib-ink-muted)]">by {n.sent_by_name}</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-[var(--lib-ink)]">{n.borrower_name}</div>
                    <div className="text-[11px] font-mono text-[var(--lib-ink-muted)]">{n.borrower_email}</div>
                  </td>
                  <td className="px-5 py-3 text-[var(--lib-ink)] truncate max-w-[260px]">{n.book_title}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs">
                    <span className={n.days_overdue_at_send > 14 ? 'text-[var(--lib-danger)] font-bold' : 'text-[var(--lib-ink)]'}>
                      {n.days_overdue_at_send}d
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-xs text-[var(--lib-ink)]">
                    ${parseFloat(n.fine_amount_at_send || '0').toFixed(2)}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${
                        STATUS_TONE[n.delivery_status] ?? 'bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)] border-[var(--lib-hairline)]'
                      }`}
                    >
                      <DeliveryIcon status={n.delivery_status} />
                      {n.delivery_status}
                    </span>
                    {n.delivery_error && (
                      <div className="text-[11px] text-[var(--lib-danger)] mt-1 truncate max-w-[200px]">{n.delivery_error}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`text-left font-mono text-[10px] uppercase tracking-wider px-5 py-3 ${className}`}>
    {children}
  </th>
);

const Kpi: React.FC<{ label: string; value: string; tone?: 'ok' | 'red' }> = ({ label, value, tone }) => (
  <div className="px-6 py-4">
    <div className={`font-display text-[28px] font-bold ${tone === 'red' ? 'text-[var(--lib-danger)]' : 'text-[var(--lib-ink)]'}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">{label}</div>
  </div>
);

const DeliveryIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'delivered' || status === 'sent') return <CheckCircle2 className="h-3 w-3" />;
  if (status === 'pending') return <Clock className="h-3 w-3" />;
  return <AlertTriangle className="h-3 w-3" />;
};

export default OverdueNoticesModule;
